import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { organizationApi } from 'src/api/organizationApi';
import { pimApi } from 'src/api/pimApi';
import {
  namedPartyFromSubscription,
  parseSubscriptionsList,
  subscriptionsApi,
} from 'src/api/subscriptionsApi';
import { userApi } from 'src/api/userApi';
import SubHeader from 'src/components/SubHeader';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { CustomButton2 } from 'src/components/widgets/buttons/CustomButton2';
import SearchBar from 'src/components/widgets/searchBar/SearchBar';
import { CLIENT_SUBSCRIPTIONS_CREATE } from 'src/constants/constants';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import useHasPermissions from 'src/hooks/useHasPermission';
import useGenericStyles from 'src/styles/useGenericStyles';

const STATUS_COLORS: Record<string, { text: string; background: string }> = {
  active: {
    text: '#10B981',
    background: '#D1FAE5',
  },
  'expiring soon': {
    text: '#F59E0B',
    background: '#FEF3C7',
  },
  expired: {
    text: '#EF4444',
    background: '#FEE2E2',
  },
};

const CHILD_COLUMNS = [
  { field: 'subscriptionOrderNumber', headerName: 'Subscription #', minWidth: 140 },
  { field: 'customerReference', headerName: 'Customer Reference', minWidth: 150 },
  { field: 'billingCycle', headerName: 'Billing Cycle', minWidth: 120 },
  { field: 'productName', headerName: 'Product / Package', minWidth: 220 },
  { field: 'numberOfLicenses', headerName: 'Licenses', minWidth: 90 },
  { field: 'createdBy', headerName: 'Created By', minWidth: 130 },
  { field: 'startDate', headerName: 'Start Date', minWidth: 120 },
  { field: 'endDate', headerName: 'End Date', minWidth: 120 },
  { field: 'status', headerName: 'Status', minWidth: 110 },
] as const;

const MISSING_CLIENT_KEY = '__missing_client__';

const uniqueIds = (values: Array<string | undefined | null>): string[] =>
  Array.from(new Set(values.map((v) => String(v || '').trim()).filter(Boolean)));

const formatPersonName = (user: any): string => {
  if (!user) return '';
  const first = user?.firstName?.trim() || '';
  const last = user?.lastName?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || user?.name || user?.email || '';
};

/** Same naming as Create Subscription product picker. */
const productLabelFromRecord = (product: any): string => {
  const template = product?.productTemplateObject || {};
  return String(
    template?.Name ||
    template?.name ||
    product?.name ||
    product?.productName ||
    product?.label ||
    product?.sku ||
    '',
  ).trim();
};

type MappedSubscriptionRow = {
  id?: string;
  _id?: string;
  clientId: string;
  clientName: string;
  subscriptionOrderNumber?: string;
  customerReference?: string;
  billingCycle?: string;
  productName?: string;
  numberOfLicenses?: number;
  createdBy?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  [key: string]: unknown;
};

type ClientSubscriptionGroup = {
  clientId: string;
  clientName: string;
  subscriptions: MappedSubscriptionRow[];
};

const mapSubscriptionRow = (
  row: any,
  names: {
    products: Record<string, string>;
    clients: Record<string, string>;
    users: Record<string, string>;
  },
): MappedSubscriptionRow => {
  const products = Array.isArray(row?.products) ? row.products : [];
  const productLabels = products
    .map((p: any) => {
      const id = String(p?.productId || '').trim();
      const name =
        p?.productName ||
        p?.packageName ||
        p?.name ||
        p?.product?.name ||
        p?.package?.name ||
        names.products[id] ||
        id;
      const qty = Number(p?.numberOfLicenses) || 0;
      if (!name) return '';
      return qty > 0 ? `${name} (${qty})` : name;
    })
    .filter(Boolean);

  const licensesFromProducts = products.reduce(
    (sum: number, p: any) => sum + (Number(p?.numberOfLicenses) || 0),
    0,
  );

  // List API omits licenses[]; use licenseCount (singular) from BE.
  const licensesCountRaw =
    row?.licenseCount ?? row?.licensesCount ?? row?.numberOfLicenses;
  const licensesCount =
    licensesCountRaw != null && licensesCountRaw !== ''
      ? Number(licensesCountRaw)
      : licensesFromProducts > 0
        ? licensesFromProducts
        : undefined;

  let billingCycle =
    row?.billingCycle || row?.subscriptionType || row?.type || '';
  if (typeof billingCycle === 'string' && billingCycle) {
    const label = billingCycle.replace(/_/g, ' ').toLowerCase();
    billingCycle = label.charAt(0).toUpperCase() + label.slice(1);
  }

  const client = namedPartyFromSubscription(row?.client, row?.clientId);
  const clientId = String(client?.id || '').trim() || MISSING_CLIENT_KEY;
  const clientName =
    client?.name ||
    row?.clientName ||
    (client?.id ? names.clients[client.id] : '') ||
    'N/A';

  const createdById = String(
    row?.createdBy?.id ||
    (typeof row?.createdBy === 'string' ? row.createdBy : '') ||
    '',
  ).trim();
  const createdByName =
    row?.createdBy?.name ||
    formatPersonName(row?.createdBy) ||
    row?.createdByName ||
    names.users[createdById] ||
    '';

  return {
    ...row,
    id: row?.id || row?._id,
    subscriptionOrderNumber:
      row?.subscriptionOrderNumber || row?.orderNumber || 'N/A',
    customerReference: row?.customerReference,
    billingCycle,
    productName:
      row?.productName ||
      (productLabels.length ? productLabels.join(', ') : undefined),
    numberOfLicenses:
      licensesCount != null && !Number.isNaN(licensesCount)
        ? licensesCount
        : undefined,
    clientId,
    clientName,
    client: clientName,
    createdBy: createdByName || undefined,
    startDate: row?.startDate,
    endDate: row?.endDate,
    status: row?.status,
  };
};

const groupSubscriptionsByClient = (
  rows: MappedSubscriptionRow[],
): ClientSubscriptionGroup[] => {
  const byClient = new Map<string, ClientSubscriptionGroup>();

  rows.forEach((row) => {
    const clientId = String(row.clientId || '').trim() || MISSING_CLIENT_KEY;
    const clientName = String(row.clientName || '').trim() || 'N/A';
    const existing = byClient.get(clientId);
    if (existing) {
      existing.subscriptions.push(row);
      return;
    }
    byClient.set(clientId, {
      clientId,
      clientName,
      subscriptions: [row],
    });
  });

  return Array.from(byClient.values()).sort((a, b) =>
    a.clientName.localeCompare(b.clientName, undefined, { sensitivity: 'base' }),
  );
};

type NameCache = {
  products: Map<string, string>;
  clients: Map<string, string>;
  users: Map<string, string>;
  productsLoaded: boolean;
  clientsParentId: string | null;
};

/**
 * FE cannot SQL-JOIN across services. Prefer BE-enriched names on the list;
 * otherwise enrich with: 1× product+package catalogue, 1× child orgs,
 * plus CASES user lookups only for creators still missing a name.
 */
async function resolveNameMaps(
  items: any[],
  cache: NameCache,
  opts: { resellerOrgId?: string; currentUserId?: string; currentUserName?: string },
): Promise<{
  products: Record<string, string>;
  clients: Record<string, string>;
  users: Record<string, string>;
}> {
  const { resellerOrgId, currentUserId, currentUserName } = opts;

  if (currentUserId && currentUserName) {
    cache.users.set(currentUserId, currentUserName);
  }

  // Seed from BE-enriched creators so we skip CASES GET /user/{id}.
  items.forEach((row) => {
    const createdBy = row?.createdBy;
    const id = String(
      typeof createdBy === 'string' ? createdBy : createdBy?.id || '',
    ).trim();
    if (!id || cache.users.has(id)) return;
    const name =
      (typeof createdBy === 'object' && createdBy
        ? String(createdBy?.name || '').trim() || formatPersonName(createdBy)
        : '') ||
      String(row?.createdByName || '').trim();
    if (name) cache.users.set(id, name);
  });

  const productIdsNeeded = uniqueIds(
    items.flatMap((row) =>
      (Array.isArray(row?.products) ? row.products : []).map(
        (p: any) => p?.productId,
      ),
    ),
  );

  items.forEach((row) => {
    const client = namedPartyFromSubscription(row?.client, row?.clientId);
    if (client?.id && client.name) cache.clients.set(client.id, client.name);
  });

  const clientIdsNeeded = uniqueIds(
    items.map((row) => namedPartyFromSubscription(row?.client, row?.clientId)?.id),
  ).filter((id) => !cache.clients.has(id));

  const userIdsNeeded = uniqueIds(
    items.map((row) =>
      typeof row?.createdBy === 'string' ? row.createdBy : row?.createdBy?.id,
    ),
  ).filter((id) => !cache.users.has(id));

  await Promise.all([
    (async () => {
      if (productIdsNeeded.length === 0) return;
      // Catalogue once per session: products by domain + PACKAGES.
      if (!cache.productsLoaded) {
        try {
          const response = await pimApi.getProductPackageGroup({
            page: 0,
            pageSize: 1000,
            isActive: true,
          });
          const root = response?.data?.data ?? response?.data ?? response ?? {};
          const groups = Array.isArray(root?.groups)
            ? root.groups
            : Array.isArray(response?.data?.groups)
              ? response.data.groups
              : [];

          groups.forEach((group: any) => {
            const groupItems = Array.isArray(group?.items) ? group.items : [];
            groupItems.forEach((entry: any) => {
              const id = String(entry?.id || entry?._id || '').trim();
              if (!id) return;
              const label = String(entry?.name || entry?.label || '').trim();
              if (label) cache.products.set(id, label);
            });
          });
          cache.productsLoaded = true;
        } catch {
          // Fallback: products-only catalogue if grouped API fails.
          try {
            const response = await pimApi.getAllProducts({
              page: 0,
              pageSize: 1000,
              isActive: true,
            });
            const list = Array.isArray(response?.data?.data)
              ? response.data.data
              : Array.isArray(response?.data)
                ? response.data
                : [];
            list.forEach((product: any) => {
              const id = String(product?.id || product?._id || '').trim();
              if (!id) return;
              const label = productLabelFromRecord(product);
              if (label) cache.products.set(id, label);
            });
            cache.productsLoaded = true;
          } catch {
            cache.productsLoaded = false;
          }
        }
      }
    })(),
    (async () => {
      if (!resellerOrgId || clientIdsNeeded.length === 0) return;
      if (cache.clientsParentId === resellerOrgId) return;
      try {
        const response = await organizationApi.getByParentId(
          resellerOrgId,
          0,
          200,
        );
        const clients = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        clients.forEach((client: any) => {
          const id = String(client?.id || client?._id || '').trim();
          const name = String(
            client?.name || client?.organizationName || '',
          ).trim();
          if (id && name) cache.clients.set(id, name);
        });
        cache.clientsParentId = resellerOrgId;
      } catch {
        cache.clientsParentId = null;
      }
    })(),
    ...userIdsNeeded.map(async (id) => {
      try {
        const user = await userApi.getById(id);
        cache.users.set(id, formatPersonName(user) || id);
      } catch {
        cache.users.set(id, id);
      }
    }),
  ]);

  return {
    products: Object.fromEntries(cache.products),
    clients: Object.fromEntries(cache.clients),
    users: Object.fromEntries(cache.users),
  };
}

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const StatusPill = ({ value }: { value?: string }) => {
  const colors =
    STATUS_COLORS[String(value || '').toLowerCase()] || {
      text: '#2196f3',
      background: '#E3F2FD',
    };
  return (
    <Box
      component="span"
      sx={{
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: colors.background,
        color: colors.text,
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'capitalize',
      }}>
      {value || 'N/A'}
    </Box>
  );
};

const renderChildCell = (
  field: (typeof CHILD_COLUMNS)[number]['field'],
  row: MappedSubscriptionRow,
) => {
  switch (field) {
    case 'subscriptionOrderNumber':
      return row.subscriptionOrderNumber || 'N/A';
    case 'customerReference':
      return row.customerReference || 'N/A';
    case 'billingCycle':
      return row.billingCycle || 'N/A';
    case 'productName': {
      const label = row.productName || 'N/A';
      return (
        <Box
          sx={{
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={label}>
          {label}
        </Box>
      );
    }
    case 'numberOfLicenses':
      return row.numberOfLicenses != null ? String(row.numberOfLicenses) : 'N/A';
    case 'createdBy':
      return row.createdBy || 'N/A';
    case 'startDate':
      return row.startDate ? formatDate(row.startDate) : 'N/A';
    case 'endDate':
      return row.endDate ? formatDate(row.endDate) : 'N/A';
    case 'status':
      return <StatusPill value={row.status} />;
    default:
      return 'N/A';
  }
};

function Subscriptions() {
  const classes = useGenericStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData } = useUserProfile();
  const orgId = userData?.organization?.id;
  const isReseller =
    userData?.organization?.organizationTypeName === 'Reseller';
  const checkPermissions = useHasPermissions();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({});
  const [data, setData] = useState<MappedSubscriptionRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(
    () => new Set(),
  );
  const nameCacheRef = useRef<NameCache>({
    products: new Map(),
    clients: new Map(),
    users: new Map(),
    productsLoaded: false,
    clientsParentId: null,
  });

  const clientGroups = useMemo(
    () => (isReseller ? groupSubscriptionsByClient(data) : []),
    [data, isReseller],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const toggleClient = (clientId: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeLimit = (event: ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!orgId) {
        setData([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await subscriptionsApi.getSubscriptions({
          page: page + 1,
          size: pageSize,
          search: search || undefined,
          ...(isReseller ? { resellerId: orgId } : { clientId: orgId }),
        });
        if (cancelled) return;
        const { items, total } = parseSubscriptionsList(response);

        const currentUserName = formatPersonName(userData);
        const names = await resolveNameMaps(items, nameCacheRef.current, {
          resellerOrgId: isReseller ? orgId : undefined,
          currentUserId: userData?.id,
          currentUserName: currentUserName || undefined,
        });
        if (cancelled) return;

        setData(items.map((row) => mapSubscriptionRow(row, names)));
        setTotalCount(total);
      } catch (error: any) {
        if (cancelled) return;
        setData([]);
        setTotalCount(0);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load subscriptions';
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, orgId, isReseller, userData]);

  const flatColumns = [
    {
      field: 'subscriptionOrderNumber',
      headerName: 'Subscription #',
      sortable: true,
      minWidth: '140px',
    },
    {
      field: 'customerReference',
      headerName: 'Customer Reference',
      sortable: true,
      width: '15%',
      minWidth: '150px',
      render: (value: any): string => value || 'N/A',
    },
    {
      field: 'billingCycle',
      headerName: 'Billing Cycle',
      sortable: true,
      minWidth: '120px',
      render: (value: any): string => value || 'N/A',
    },
    {
      field: 'productName',
      headerName: 'Product / Package',
      sortable: true,
      width: '300px',
      minWidth: '220px',
      render: (value: any): JSX.Element => {
        const label = value || 'N/A';
        return (
          <Box
            sx={{
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={label}>
            {label}
          </Box>
        );
      },
    },
    {
      field: 'numberOfLicenses',
      headerName: 'Licenses',
      sortable: true,
      minWidth: '90px',
      render: (value: any): string => (value != null ? String(value) : 'N/A'),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      sortable: true,
      minWidth: '130px',
      render: (value: any): string => value?.name || value || 'N/A',
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      sortable: true,
      minWidth: '120px',
      render: (value: any): string => (value ? formatDate(value) : 'N/A'),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      sortable: true,
      minWidth: '120px',
      render: (value: any): string => (value ? formatDate(value) : 'N/A'),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      minWidth: '110px',
      render: (value: any): JSX.Element => <StatusPill value={value} />,
    },
  ];

  const colSpan = CHILD_COLUMNS.length + 1;

  return (
    <>
      <Helmet>
        <title>Mindtime User Portal | Subscriptions</title>
      </Helmet>
      <SubHeader title="Subscriptions" />
      <Box px="30px">
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            mb: '10px',
            gap: '10px',
          }}>
          <SearchBar
            value={search}
            onSearch={handleSearch}
            placeholderData="Search subscriptions..."
          />
          {checkPermissions([CLIENT_SUBSCRIPTIONS_CREATE]) && (
            <CustomButton2
              backgroundColor={theme.palette.primary.main}
              className={classes.button}
              onClick={() => {
                navigate('/create-subscription/quick');
              }}>
              <AddIcon
                sx={{
                  marginRight: '5px',
                }}
              />{' '}
              Create Subscription
            </CustomButton2>
          )}
        </Box>

        {isReseller ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: 'text.primary',
                }}>
                All Subscriptions
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small" sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        minWidth: 200,
                        pl: 1.5,
                      }}>
                      Client
                    </TableCell>
                    {CHILD_COLUMNS.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          minWidth: column.minWidth,
                        }}>
                        {column.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : clientGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" fontSize={13}>
                          No subscriptions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    clientGroups.map((group) => {
                      const isExpanded = expandedClients.has(group.clientId);
                      return (
                        <Fragment key={group.clientId}>
                          <TableRow
                            onClick={() => toggleClient(group.clientId)}
                            sx={{
                              cursor: 'pointer',
                              bgcolor: '#fff',
                              '&:hover': {
                                bgcolor: '#fff',
                              },
                            }}>
                            <TableCell sx={{ py: 1, pl: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleClient(group.clientId);
                                  }}
                                  sx={{ p: '2px' }}>
                                  {isExpanded ? (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  ) : (
                                    <KeyboardArrowRightIcon fontSize="small" />
                                  )}
                                </IconButton>
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={group.clientName}>
                                  {group.clientName}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    ml: 1,
                                    fontSize: '12px',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                  }}>
                                  ({group.subscriptions.length})
                                </Typography>
                              </Box>
                            </TableCell>
                            {CHILD_COLUMNS.map((column) => (
                              <TableCell key={column.field} />
                            ))}
                          </TableRow>

                          {isExpanded &&
                            group.subscriptions.map((row) => {
                              const subscriptionId = row.id || row._id;
                              return (
                                <TableRow
                                  key={String(subscriptionId)}
                                  hover
                                  onClick={() => {
                                    if (subscriptionId) {
                                      navigate(`/subscriptions/${subscriptionId}/overview`);
                                    }
                                  }}
                                  sx={{ cursor: 'pointer' }}>
                                  <TableCell sx={{ pl: 5 }} />
                                  {CHILD_COLUMNS.map((column) => (
                                    <TableCell
                                      key={column.field}
                                      sx={{
                                        fontSize: '13px',
                                        py: 1.25,
                                      }}>
                                      {renderChildCell(column.field, row)}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 3,
              }}>
              <Typography
                className={classes.description}
                color="#7A7A9D!important">
                {`Showing ${data.length} items out of ${totalCount} results found`}
              </Typography>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100, 500, 1000]}
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeLimit}
                labelDisplayedRows={({ count }) =>
                  `Page ${page + 1} of ${Math.max(1, Math.ceil(count / pageSize) || 1)}`
                }
              />
            </Box>
          </Paper>
        ) : (
          <CustomizedTable
            heading="All Subscriptions"
            loading={loading}
            columns={flatColumns}
            page={page}
            limit={pageSize}
            handleLimit={setPageSize}
            handlePage={setPage}
            data={data}
            totalRecords={totalCount}
            handleSort={setSort}
            sort={sort}
            customizableColumns
            bodyCellPadding="10px 12px"
            headerCellPadding="10px 12px"
            handleOnClick={(row) => {
              const id = row?.id || row?._id;
              if (id) navigate(`/subscriptions/${id}/overview`);
            }}
          />
        )}
      </Box>
    </>
  );
}

export default Subscriptions;
